/**
 * size-unit.js — the grams / ounces toggle on the variant picker.
 *
 * Bemka merchandises caviar in variant option values like "1 oz (28g)". Some
 * shoppers think in grams, some in ounces, so the source let them flip the
 * displayed unit and remembered the choice in a `size-unit` cookie for a year.
 * That cookie name, lifetime and SameSite policy are preserved here so a
 * shopper's preference survives the migration from the Next.js storefront.
 *
 * ⚠ THIS PARSER IS FRAGILE BY NATURE — it reads meaning out of merchandising
 * copy. It recognises "1 oz (28g)", "1 oz" and "28g"; a merchandiser who
 * renames a variant to "1oz(28 g)", "1 ounce (28 grams)" or "28 gr" falls
 * outside those patterns. When that happens this file does NOT throw and does
 * NOT blank the label: the raw option string stays on screen exactly as the
 * merchandiser typed it, and a warning is logged ONLY inside the theme editor
 * (design mode) where the person who can fix the naming will see it. Shoppers
 * never see a console message and never see an empty size button.
 *
 * A toggle is shown for an option group only when at least one of its values
 * parses into two DIFFERENT strings — matching the source's `isSizeGroup`,
 * which required `parsed.oz !== parsed.g`. A "Colour" option never sprouts a
 * g/oz switch.
 */
(function () {
  'use strict';

  var COOKIE_NAME = 'size-unit';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  var BOUND = 'sizeUnitBound';

  function readCookie(name) {
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/[-.]/g, '\\$&') + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value) {
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      '; path=/; max-age=' +
      COOKIE_MAX_AGE +
      '; SameSite=Lax';
  }

  /**
   * Parse "1 oz (28g)" into { oz: "1 oz", g: "28g" }.
   * Also handles "1 oz" and "28g" / "28 g". Returns null when nothing matches —
   * callers must treat null as "leave the label alone".
   */
  function parseSizeValue(value) {
    if (typeof value !== 'string') return null;

    var ozWithG = value.match(/^([\d.]+\s*oz)\s*\((\d+\s*g)\)$/i);
    if (ozWithG) {
      return { oz: ozWithG[1].trim(), g: ozWithG[2].trim() };
    }

    var ozOnly = value.match(/^([\d.]+\s*oz)$/i);
    if (ozOnly) {
      return { oz: ozOnly[1].trim(), g: value };
    }

    var gOnly = value.match(/^(\d+\s*g)$/i);
    if (gOnly) {
      return { oz: value, g: gOnly[1].trim() };
    }

    return null;
  }

  function currentUnit(group) {
    var saved = readCookie(COOKIE_NAME);
    if (saved === 'g' || saved === 'oz') return saved;

    var fallback = group.getAttribute('data-default-unit');
    return fallback === 'oz' ? 'oz' : 'g';
  }

  function isDesignMode(group) {
    if (window.Shopify && window.Shopify.designMode) return true;
    return group.getAttribute('data-design-mode') === 'true';
  }

  function setupGroup(group) {
    if (group.dataset[BOUND] === 'true') return;
    group.dataset[BOUND] = 'true';

    var labels = Array.prototype.slice.call(
      group.querySelectorAll('[data-size-label]')
    );
    if (labels.length === 0) return;

    var toggle = group.querySelector('[data-size-unit-toggle]');
    var parsedByLabel = [];
    var isSizeGroup = false;
    var unparsed = [];

    labels.forEach(function (label) {
      var raw = label.getAttribute('data-size-raw') || label.textContent;
      var parsed = parseSizeValue(raw);

      parsedByLabel.push({ label: label, raw: raw, parsed: parsed });

      if (parsed) {
        if (parsed.oz !== parsed.g) isSizeGroup = true;
      } else {
        unparsed.push(raw);
      }
    });

    if (!isSizeGroup) return;

    if (unparsed.length && isDesignMode(group)) {
      /* eslint-disable-next-line no-console */
      console.warn(
        '[Bemka] The grams/ounces toggle could not read these variant option ' +
          'values, so they are shown exactly as typed: ' +
          unparsed.join(', ') +
          '. Rename them to the "1 oz (28g)" pattern to make them switchable.'
      );
    }

    function apply(unit) {
      parsedByLabel.forEach(function (entry) {
        /* No parse → the raw merchandiser string stays on screen. */
        entry.label.textContent = entry.parsed ? entry.parsed[unit] : entry.raw;
      });

      if (toggle) {
        toggle.hidden = false;
        Array.prototype.forEach.call(
          toggle.querySelectorAll('[data-size-unit]'),
          function (button) {
            var isActive = button.getAttribute('data-size-unit') === unit;
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            button.classList.toggle('is-active', isActive);
          }
        );
      }
    }

    apply(currentUnit(group));

    if (toggle) {
      toggle.addEventListener('click', function (event) {
        var button = event.target.closest('[data-size-unit]');
        if (!button || !toggle.contains(button)) return;

        var unit = button.getAttribute('data-size-unit') === 'oz' ? 'oz' : 'g';
        writeCookie(COOKIE_NAME, unit);
        document.dispatchEvent(
          new CustomEvent('bemka:size-unit-change', { detail: { unit: unit } })
        );
      });
    }

    /* Keep every toggle on the page in step with every other one. */
    document.addEventListener('bemka:size-unit-change', function (event) {
      apply(event.detail && event.detail.unit === 'oz' ? 'oz' : 'g');
    });
  }

  function init(scope) {
    var context = scope || document;
    var groups = context.querySelectorAll('[data-size-unit-group]');
    Array.prototype.forEach.call(groups, setupGroup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
